import React, { useEffect, useRef, useState } from 'react';
import { diffWords } from 'diff';

const ResumeComparison = ({ originalResumeContent, polishedResumeContent }) => {
  const [diffSections, setDiffSections] = useState({});
  const originalScrollRef = useRef(null);
  const polishedScrollRef = useRef(null);

  useEffect(() => {
    if (originalResumeContent && polishedResumeContent) {
      generateDiffs();
    }
  }, [originalResumeContent, polishedResumeContent]);

  // Handle synchronized scrolling
  const handleScroll = (event) => {
    const { target } = event;
    const otherContainer = target === originalScrollRef.current 
      ? polishedScrollRef.current 
      : originalScrollRef.current;
    
    if (otherContainer) {
      // Synchronize scroll position
      otherContainer.scrollTop = target.scrollTop;
    }
  };

  const generateDiffs = () => {
    // Initialize diff sections
    const sections = {};
    
    // Generate diffs for each main section if available
    if (originalResumeContent?.summary && polishedResumeContent?.summary) {
      // Use diffWords for the summary section - compares changes at word level rather than character level
      sections.summary = diffWords(originalResumeContent.summary, polishedResumeContent.summary);
    }
    
    // Generate experience diffs
    if (originalResumeContent?.experience && polishedResumeContent?.experience) {
      sections.experience = [];
      
      // Compare each job
      const maxJobs = Math.max(
        originalResumeContent.experience.length,
        polishedResumeContent.experience.length
      );
      
      for (let i = 0; i < maxJobs; i++) {
        const originalJob = originalResumeContent.experience[i] || {};
        const polishedJob = polishedResumeContent.experience[i] || {};
        
        const jobDiff = {
          company: diffWords(originalJob.company || '', polishedJob.company || ''),
          position: diffWords(originalJob.position || '', polishedJob.position || ''),
          details: []
        };
        
        // Add description diff if it exists
        if (originalJob.description || polishedJob.description) {
          jobDiff.description = diffWords(originalJob.description || '', polishedJob.description || '');
        }
        
        // Compare details/bullets
        const originalDetails = originalJob.details || [];
        const polishedDetails = polishedJob.details || [];
        const maxDetails = Math.max(originalDetails.length, polishedDetails.length);
        
        for (let j = 0; j < maxDetails; j++) {
          jobDiff.details.push(
            diffWords(originalDetails[j] || '', polishedDetails[j] || '')
          );
        }
        
        // Compare projects if they exist
        if (originalJob.projects || polishedJob.projects) {
          jobDiff.projects = [];
          const originalProjects = originalJob.projects || [];
          const polishedProjects = polishedJob.projects || [];
          const maxProjects = Math.max(originalProjects.length, polishedProjects.length);
          
          for (let k = 0; k < maxProjects; k++) {
            const originalProject = originalProjects[k] || {};
            const polishedProject = polishedProjects[k] || {};
            
            const projectDiff = {
              title: diffWords(originalProject.title || '', polishedProject.title || ''),
              details: []
            };
            
            // Add description diff if it exists
            if (originalProject.description || polishedProject.description) {
              projectDiff.description = diffWords(originalProject.description || '', polishedProject.description || '');
            }
            
            // Compare project details
            const origProjDetails = originalProject.details || [];
            const polishedProjDetails = polishedProject.details || [];
            const maxProjDetails = Math.max(origProjDetails.length, polishedProjDetails.length);
            
            for (let l = 0; l < maxProjDetails; l++) {
              projectDiff.details.push(
                diffWords(origProjDetails[l] || '', polishedProjDetails[l] || '')
              );
            }
            
            jobDiff.projects.push(projectDiff);
          }
        }
        
        sections.experience.push(jobDiff);
      }
    }
    
    // Add skills comparison (supports both flat string[] and grouped { type, details }[])
    if (originalResumeContent?.skills && polishedResumeContent?.skills) {
      const origList = normalizeSkillsToList(originalResumeContent.skills);
      const polList = normalizeSkillsToList(polishedResumeContent.skills);
      const origSet = new Set(origList);
      const polSet = new Set(polList);
      const removed = origList.filter((s) => !polSet.has(s));
      const added = polList.filter((s) => !origSet.has(s));
      const unchanged = origList.filter((s) => polSet.has(s));

      const origGrouped = isGroupedSkills(originalResumeContent.skills);
      const polGrouped = isGroupedSkills(polishedResumeContent.skills);
      if (origGrouped || polGrouped) {
        sections.skills = {
          grouped: true,
          original: originalResumeContent.skills,
          polished: polishedResumeContent.skills,
          removedSet: new Set(removed),
          addedSet: new Set(added),
          unchangedSet: new Set(unchanged),
        };
      } else {
        sections.skills = { added, removed, unchanged };
      }
    }

    setDiffSections(sections);
  };

  const isGroupedSkills = (skills) =>
    Array.isArray(skills) && skills.length > 0 && skills.every((s) => s && typeof s === 'object' && Array.isArray(s.details));

  /** Flatten skills to a list of trimmed strings for comparison (handles both flat string[] and grouped { type, details }[]). */
  const normalizeSkillsToList = (skills) => {
    if (!Array.isArray(skills) || skills.length === 0) return [];
    const first = skills[0];
    if (typeof first === 'string') {
      return skills.filter((s) => typeof s === 'string').map((s) => s.trim()).filter(Boolean);
    }
    if (first && typeof first === 'object' && Array.isArray(first.details)) {
      return skills.flatMap((g) => (g.details || []).filter((d) => typeof d === 'string').map((d) => d.trim()).filter(Boolean));
    }
    return [];
  };

  // Render a diff array for the original resume (show only removed and unchanged)
  const renderOriginalDiffArray = (diffArray) => {
    if (!diffArray || !Array.isArray(diffArray)) {
      return null;
    }
    
    return diffArray.map((part, index) => (
      <React.Fragment key={index}>
        {renderOriginalDiffPart(part)}
      </React.Fragment>
    ));
  };

  // Render a diff part for the original resume (show only removed in red and unchanged)
  const renderOriginalDiffPart = (part) => {
    if (!part) {
      return null;
    }
    
    if (part.removed) {
      return <span className="bg-red-100 text-red-800">{part.value}</span>;
    }
    if (!part.added) {
      return <span>{part.value}</span>;
    }
    // Skip added parts in the original
    return null;
  };

  // Render a diff array for the polished resume (show only added and unchanged)
  const renderPolishedDiffArray = (diffArray) => {
    if (!diffArray || !Array.isArray(diffArray)) {
      return null;
    }
    
    return diffArray.map((part, index) => (
      <React.Fragment key={index}>
        {renderPolishedDiffPart(part)}
      </React.Fragment>
    ));
  };

  // Render a diff part for the polished resume (show only added in green and unchanged)
  const renderPolishedDiffPart = (part) => {
    if (!part) {
      return null;
    }
    
    if (part.added) {
      return <span className="bg-green-100 text-green-800">{part.value}</span>;
    }
    if (!part.removed) {
      return <span>{part.value}</span>;
    }
    // Skip removed parts in the polished
    return null;
  };

  // Render details for original resume
  const renderOriginalDetails = (details) => {
    if (!details || !Array.isArray(details)) {
      return null;
    }
    
    return (
      <ul className="ml-5 list-disc">
        {details.map((detail, index) => (
          <li key={index} className="mb-1">
            {renderOriginalDiffArray(detail)}
          </li>
        ))}
      </ul>
    );
  };

  // Render details for polished resume
  const renderPolishedDetails = (details) => {
    if (!details || !Array.isArray(details)) {
      return null;
    }
    
    return (
      <ul className="ml-5 list-disc">
        {details.map((detail, index) => (
          <li key={index} className="mb-1">
            {renderPolishedDiffArray(detail)}
          </li>
        ))}
      </ul>
    );
  };

  // Render projects for original resume
  const renderOriginalProjects = (projects) => {
    if (!projects || !Array.isArray(projects)) {
      return null;
    }
    
    return projects.map((project, index) => (
      <div key={index} className="ml-4 mt-2 mb-3">
        <div className="font-medium">
          {project.title ? renderOriginalDiffArray(project.title) : "Untitled Project"}
        </div>
        {project.description && (
          <div className="italic mb-1">
            {renderOriginalDiffArray(project.description)}
          </div>
        )}
        {project.details && Array.isArray(project.details) && (
          <ul className="ml-5 list-disc">
            {project.details.map((detail, idx) => (
              <li key={idx} className="mb-1">
                {renderOriginalDiffArray(detail)}
              </li>
            ))}
          </ul>
        )}
      </div>
    ));
  };

  // Render projects for polished resume
  const renderPolishedProjects = (projects) => {
    if (!projects || !Array.isArray(projects)) {
      return null;
    }
    
    return projects.map((project, index) => (
      <div key={index} className="ml-4 mt-2 mb-3">
        <div className="font-medium">
          {project.title ? renderPolishedDiffArray(project.title) : "Untitled Project"}
        </div>
        {project.description && (
          <div className="italic mb-1">
            {renderPolishedDiffArray(project.description)}
          </div>
        )}
        {project.details && Array.isArray(project.details) && (
          <ul className="ml-5 list-disc">
            {project.details.map((detail, idx) => (
              <li key={idx} className="mb-1">
                {renderPolishedDiffArray(detail)}
              </li>
            ))}
          </ul>
        )}
      </div>
    ));
  };

  // Render a job for original resume
  const renderOriginalJob = (job, index) => {
    if (!job) {
      return null;
    }
    
    return (
      <div key={index} className="mb-4 pb-4 border-b border-gray-200 last:border-b-0">
        <div className="flex justify-between mb-1">
          <span className="font-bold">
            {job.company ? renderOriginalDiffArray(job.company) : "Unknown Company"}
          </span>
          <span className="italic">
            {job.position ? renderOriginalDiffArray(job.position) : "Unknown Position"}
          </span>
        </div>
        {job.description && (
          <div className="mb-2">{renderOriginalDiffArray(job.description)}</div>
        )}
        {job.details && renderOriginalDetails(job.details)}
        {job.projects && renderOriginalProjects(job.projects)}
      </div>
    );
  };

  // Render a job for polished resume
  const renderPolishedJob = (job, index) => {
    if (!job) {
      return null;
    }
    
    return (
      <div key={index} className="mb-4 pb-4 border-b border-gray-200 last:border-b-0">
        <div className="flex justify-between mb-1">
          <span className="font-bold">
            {job.company ? renderPolishedDiffArray(job.company) : "Unknown Company"}
          </span>
          <span className="italic">
            {job.position ? renderPolishedDiffArray(job.position) : "Unknown Position"}
          </span>
        </div>
        {job.description && (
          <div className="mb-2">{renderPolishedDiffArray(job.description)}</div>
        )}
        {job.details && renderPolishedDetails(job.details)}
        {job.projects && renderPolishedProjects(job.projects)}
      </div>
    );
  };

  // Render grouped skills with per-skill coloring: each skill tag is red (removed), gray (unchanged), or green (added)
  const renderGroupedSkillsWithDiff = (skills, mode, diffData) => {
    if (!Array.isArray(skills) || skills.length === 0) return null;
    const removedSet = diffData?.removedSet ?? new Set();
    const addedSet = diffData?.addedSet ?? new Set();
    return (
      <div className="mb-4">
        <h5 className="text-sm font-medium border-b border-gray-200 pb-1 mb-2">Skills</h5>
        <div className="space-y-2">
          {skills.map((group, gi) => {
            const type = group?.type ?? 'Skills';
            const details = Array.isArray(group?.details) ? group.details : [];
            return (
              <div key={`group-${gi}-${type}`}>
                {type && <span className="text-xs font-medium text-gray-600 mr-2">{type}</span>}
                <div className="flex flex-wrap gap-2 mt-1">
                  {details.filter((d) => typeof d === 'string' && d.trim()).map((skill, si) => {
                    const trimmed = String(skill).trim();
                    let tagClassName = 'bg-gray-100 text-gray-800';
                    if (mode === 'original') {
                      tagClassName = removedSet.has(trimmed) ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800';
                    } else {
                      tagClassName = addedSet.has(trimmed) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
                    }
                    return (
                      <span key={`${gi}-${si}-${skill}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tagClassName}`}>
                        {skill}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render skills comparison - original shows removed (red) and unchanged (gray)
  const renderOriginalSkills = (skillsSection) => {
    if (!skillsSection) return null;
    if (skillsSection.grouped && skillsSection.original) {
      return renderGroupedSkillsWithDiff(skillsSection.original, 'original', skillsSection);
    }
    return (
      <div className="mb-4">
        <h5 className="text-sm font-medium border-b border-gray-200 pb-1 mb-2">Skills</h5>
        <div className="flex flex-wrap gap-2">
          {skillsSection.removed && skillsSection.removed.map((skill, i) => (
            <span key={`removed-${i}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              {typeof skill === 'string' ? skill : (skill?.type || 'Skill')}
            </span>
          ))}
          {skillsSection.unchanged && skillsSection.unchanged.map((skill, i) => (
            <span key={`unchanged-${i}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              {typeof skill === 'string' ? skill : (skill?.type || 'Skill')}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // Render skills comparison - polished shows added (green) and unchanged (gray)
  const renderPolishedSkills = (skillsSection) => {
    if (!skillsSection) return null;
    if (skillsSection.grouped && skillsSection.polished) {
      return renderGroupedSkillsWithDiff(skillsSection.polished, 'polished', skillsSection);
    }
    return (
      <div className="mb-4">
        <h5 className="text-sm font-medium border-b border-gray-200 pb-1 mb-2">Skills</h5>
        <div className="flex flex-wrap gap-2">
          {skillsSection.added && skillsSection.added.map((skill, i) => (
            <span key={`added-${i}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {typeof skill === 'string' ? skill : (skill?.type || 'Skill')}
            </span>
          ))}
          {skillsSection.unchanged && skillsSection.unchanged.map((skill, i) => (
            <span key={`unchanged-${i}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              {typeof skill === 'string' ? skill : (skill?.type || 'Skill')}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <h3 className="text-base font-medium text-gray-900 mb-4">Resume Comparison</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Original Resume with Only Removed (Red) Content */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Original Resume</h4>
          <div 
            ref={originalScrollRef}
            onScroll={handleScroll} 
            className="p-3 rounded-md h-96 overflow-y-auto text-sm"
          >
            {originalResumeContent && Object.keys(diffSections).length > 0 ? (
              <div className="space-y-4">
                {/* Summary Section */}
                {diffSections.summary && (
                  <div className="mb-4">
                    <h5 className="text-sm font-medium border-b border-gray-200 pb-1 mb-2">Summary</h5>
                    <p>{renderOriginalDiffArray(diffSections.summary)}</p>
                  </div>
                )}
                
                {/* Skills Section */}
                {diffSections.skills && (
                  renderOriginalSkills(diffSections.skills)
                )}
                
                {/* Experience Section */}
                {diffSections.experience && diffSections.experience.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium border-b border-gray-200 pb-1 mb-2">Experience</h5>
                    {diffSections.experience.map((job, index) => renderOriginalJob(job, index))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">Original resume content not available</p>
            )}
          </div>
        </div>
        
        {/* Polished Resume with Only Added (Green) Content */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Polished Resume</h4>
          <div 
            ref={polishedScrollRef}
            onScroll={handleScroll} 
            className="p-3 rounded-md h-96 overflow-y-auto text-sm"
          >
            {polishedResumeContent && Object.keys(diffSections).length > 0 ? (
              <div className="space-y-4">
                {/* Summary Section */}
                {diffSections.summary && (
                  <div className="mb-4">
                    <h5 className="text-sm font-medium border-b border-gray-200 pb-1 mb-2">Summary</h5>
                    <p>{renderPolishedDiffArray(diffSections.summary)}</p>
                  </div>
                )}
                
                {/* Skills Section */}
                {diffSections.skills && (
                  renderPolishedSkills(diffSections.skills)
                )}
                
                {/* Experience Section */}
                {diffSections.experience && diffSections.experience.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium border-b border-gray-200 pb-1 mb-2">Experience</h5>
                    {diffSections.experience.map((job, index) => renderPolishedJob(job, index))}
                  </div>
                )}
              </div>
            ) : (
              <div>
                {polishedResumeContent ? (
                  <p className="text-gray-500 mb-2">Loading differences...</p>
                ) : (
                  <p className="text-gray-500">Polished resume content not available</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="mt-4 text-xs text-gray-500">
        <p>
          <span className="inline-block bg-red-100 text-red-800 px-2 py-0.5 rounded mr-2">Red</span> indicates content removed from original
          <span className="inline-block bg-green-100 text-green-800 px-2 py-0.5 rounded mr-2 ml-4">Green</span> indicates content added to polished
        </p>
      </div>
    </div>
  );
};

export default ResumeComparison;